import { Builder } from '@builder.io/react';
import React from 'react';

import KeyboardArrowRightIcon from '@videoblocks/react-icons/KeyboardArrowRight';

import VideoStockItemCard from '../../../../app/Search/components/card/VideoStockItemCard';
import {
  frameRates as frameRateOptions,
  getCategoriesForContentType,
  contentTypes,
  qualities as videoResolutionOptions,
} from '../../../../app/Search/entities/VideoSearchFilterOptions';
import {
  defaultSortOptions,
  stockItemCardResultOptions,
  videoSortOptions,
} from '../../../constants';
import {
  useVideoGridContentFetching,
  type ContentFetchingParams,
} from '../../../hooks/useVideoGridContentFetching';
import { useVideoGridSchema } from '../../../hooks/useVideoGridSchema';
import { normalizeStockItems } from '../../../utils/contentUtils';
import BuilderSection from '../../layouts/BuilderSection';
import StockItemCardLoader from '../../loading-skeletons/StockItemCardLoader';

interface VideoContentGridProps {
  customContent?: any;
  category: string;
  frameRates: string[];
  heading: string;
  exploreLink?: string;
  exploreText?: string;
  mediaType: string;
  minQualityScore?: number;
  numResults: string;
  resolution: string;
  searchTerm: string;
  sort: string;
  contentMode?: 'search' | 'collection' | 'mixed';
  galleryName?: string;
}

const VideoContentGrid = (props: VideoContentGridProps) => {
  const {
    customContent,
    category,
    frameRates,
    heading,
    exploreLink,
    exploreText,
    mediaType,
    minQualityScore,
    numResults,
    resolution,
    searchTerm,
    sort,
    contentMode = 'search',
    galleryName = '',
  } = props;

  const hookParams: ContentFetchingParams = {
    category,
    mediaType,
    frameRates,
    searchTerm,
    sort,
    minQualityScore,
    resolution,
    galleryName,
    numResults,
    contentMode,
    customContent: customContent ? normalizeStockItems(customContent) : [],
    builderIsEditing: !!Builder.isEditing,
  };

  const {
    content: displayContent,
    loading: displayLoading,
    error,
  } = useVideoGridContentFetching(hookParams);

  useVideoGridSchema({
    heading,
    videos: displayContent || [],
    contentMode,
    category,
    searchTerm,
  });

  const parsedNumResults = parseInt(numResults, 10);

  return (
    <BuilderSection
      heading={heading}
      headingClassNames="text-gray-900 text-left font-sans text-2xl font-semibold"
    >
      <div className="w-full space-y-6">
        <div className="stock-item-group-wrapper card-design leading">
          {displayLoading ? (
            <div className="grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {[...Array(parsedNumResults)].map((_, index) => (
                  <StockItemCardLoader
                    key={`video-card-loader-${numResults + index}`}
                  />
                ))}
              </div>
            </div>
          ) : displayContent?.length < 1 ? (
            <div>No results found{error ? `: ${error}` : ''}</div>
          ) : (
            <div className="stock-item-v2 grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {displayContent.map((item, index) => (
                  <VideoStockItemCard
                    {...item}
                    className="m-0 p-0"
                    shouldInvertControlsVisibility={true}
                    thumbnailClassNames="overflow-hidden rounded-lg"
                    key={`video-stock-item-card-${item.stockItem.id || index}`}
                    isFocusOutlineVisible
                    isSelected={false}
                    page={null}
                    hideOnThreeColumnLayout={false}
                    shouldStockItemHaveNewFlag={false}
                    redirectToSignUpLink={false}
                    stockItemFormats={[]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {exploreText?.length && (
          <a
            href={exploreLink}
            className="flex items-center float-right gap-1 text-base font-bold text-gray-700 cursor-pointer"
          >
            {exploreText}
            <KeyboardArrowRightIcon className="fill-current w-3.5" />
          </a>
        )}
      </div>
    </BuilderSection>
  );
};

Builder.registerComponent(VideoContentGrid, {
  name: 'Video Content Grid (Legacy)',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'Browse Videos',
    },
    {
      name: 'numResults',
      friendlyName: 'Number of Results',
      type: 'string',
      enum: stockItemCardResultOptions,
      defaultValue: '9',
      required: true,
    },
    {
      name: 'contentMode',
      type: 'string',
      enum: [
        { label: 'Search', value: 'search' },
        { label: 'Collection', value: 'collection' },
        { label: 'Mixed', value: 'mixed' },
      ],
      defaultValue: 'search',
      required: true,
      friendlyName: 'Content Mode',
    },
    {
      name: 'galleryName',
      type: 'string',
      defaultValue: '',
      required: false,
      showIf: (options) => options.get('contentMode') !== 'search',
      friendlyName: 'Collection Slug (URI)',
      helperText:
        'Enter the collection slug/URI (the part after /video/collections/ in the URL)',
    },
    {
      name: 'exploreText',
      type: 'string',
      required: true,
      defaultValue: 'Explore all content',
    },
    {
      name: 'exploreLink',
      type: 'string',
      defaultValue: '',
      showIf: (options) => options.get('exploreText'),
    },
    {
      name: 'mediaType',
      type: 'string',
      required: true,
      enum: contentTypes,
      defaultValue: 'all-videos',
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'searchTerm',
      type: 'string',
      defaultValue: '',
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'resolution',
      type: 'string',
      enum: videoResolutionOptions,
      defaultValue: '',
    },
    {
      name: 'frameRate',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'rate',
          type: 'string',
          enum: frameRateOptions,
        },
      ],
    },
    {
      name: 'category',
      type: 'string',
      showIf: (options) => options.get('mediaType') === 'all-videos',
      enum: getCategoriesForContentType('all-videos'),
      defaultValue: '',
    },
    {
      name: 'category',
      type: 'string',
      showIf: (options) => options.get('mediaType') === 'footage',
      enum: getCategoriesForContentType('footage'),
      defaultValue: '',
    },
    {
      name: 'category',
      type: 'string',
      showIf: (options) => options.get('mediaType') === 'motion-backgrounds',
      enum: getCategoriesForContentType('motion-backgrounds'),
      defaultValue: '',
    },
    {
      name: 'category',
      type: 'string',
      showIf: (options) => options.get('mediaType') === 'templates',
      enum: getCategoriesForContentType('templates'),
      defaultValue: '',
    },
    {
      name: 'sort',
      type: 'string',
      defaultValue: 'most_relevant',
      required: true,
      enum: [...defaultSortOptions, ...videoSortOptions],
    },
    {
      name: 'minQualityScore',
      type: 'number',
      defaultValue: null,
      min: 0,
      max: 1,
    },
  ],
});
